import * as React from "react"
import { useCallback, useEffect, useState, useMemo } from "react"
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Grid
} from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { editMappingDrawer, imageComparisonModal } from "../@signals"
import SkuOption from "../../ProductsReview/EditMappingDrawer/SkuOption"
import Raw2RawEditor from "./Raw2RawEditor"


export default function EditMappingDrawer() {
    const { showToast } = useAppToast()
    const drawerData = editMappingDrawer.value
    const isOpen = drawerData !== null

    const [allSkuOptions, setAllSkuOptions] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [selectedSkuId, setSelectedSkuId] = useState(null)
    const [visibleCount, setVisibleCount] = useState(30)

    const raw = drawerData?.raw
    const currentSku = drawerData?.sku
    const brandId = raw?.id_brand
    const isRaw2raw = drawerData?.mappingType === 'raw2raw'

    // Filter SKUs locally based on search input (AND with comma)
    const filteredSkuOptions = useMemo(() => {
        if (!searchInput.trim()) return allSkuOptions

        const searchTerms = searchInput.toLowerCase().split(',').map(t => t.trim()).filter(Boolean)
        if (searchTerms.length === 0) return allSkuOptions

        const skuMatchesTerm = (sku, term) =>
            (sku.skuName && sku.skuName.toLowerCase().includes(term)) ||
            (sku.ean && sku.ean.toLowerCase().includes(term)) ||
            (sku.reference && sku.reference.toLowerCase().includes(term)) ||
            (sku.skuID && String(sku.skuID).includes(term))

        return allSkuOptions.filter(sku =>
            searchTerms.every(term => skuMatchesTerm(sku, term))
        )
    }, [allSkuOptions, searchInput])

    const visibleSkuOptions = useMemo(() => {
        return filteredSkuOptions.slice(0, visibleCount)
    }, [filteredSkuOptions, visibleCount])

    const hasMoreToShow = filteredSkuOptions.length > visibleCount

    // Load all SKUs once when drawer opens
    const loadSkus = useCallback(() => {
        if (!brandId) return

        setIsLoading(true)
        postSubmit(`${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandId}`, {})
            .then(res => setAllSkuOptions(res?.options || []))
            .catch(() => {
                showToast.error('Error loading SKUs')
                setAllSkuOptions([])
            })
            .finally(() => setIsLoading(false))
    }, [brandId])

    useEffect(() => {
        if (isOpen && !isRaw2raw && brandId) loadSkus()
    }, [isOpen, isRaw2raw, brandId])

    // Reset state when drawer closes
    useEffect(() => {
        if (!isOpen) {
            setAllSkuOptions([])
            setSearchInput('')
            setSelectedSkuId(null)
            setVisibleCount(30)
        }
    }, [isOpen])

    useEffect(() => { setVisibleCount(30) }, [searchInput])

    const handleClose = () => { editMappingDrawer.value = null }

    const handleImageClick = (sku) => {
        imageComparisonModal.value = {
            title: raw?.title,
            variantRaw: raw?.variant_raw,
            rawImage: raw?.image,
            skuImage: `${BACKEND_APIS.BACKEND}/sku/image/${sku.skuID}`,
            skuName: sku.skuName
        }
    }

    const handleSave = async () => {
        if (isSaving || !selectedSkuId) return

        const selectedSkuData = allSkuOptions.find(s => s.skuID === selectedSkuId)

        setIsSaving(true)
        try {
            await postSubmit(`/api/mapping/sku`, {
                productID: raw?.product_id,
                siteID: raw?.id_site,
                skuID: selectedSkuId,
                source: 'audit-log'
            })

            showToast.success('Mapping updated successfully')
            handleClose()
        } catch (error) {
            showToast.error('Error updating mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const handleRemoveMapping = async () => {
        setIsSaving(true)
        try {
            await postSubmit(`/api/mapping/sku`, {
                productID: raw?.product_id,
                siteID: raw?.id_site,
                skuID: 0,
                source: 'audit-log'
            })

            showToast.success('Mapping removed')
            handleClose()
        } catch (error) {
            showToast.error('Error removing mapping')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 600, md: 700 } }
            }}
        >
            {isOpen && isRaw2raw && (
                <Raw2RawEditor drawerData={drawerData} onClose={handleClose} />
            )}
            {isOpen && !isRaw2raw && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Edit Mapping
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <IconSeeker icon="xmark" />
                        </IconButton>
                    </Box>

                    {/* Raw Product Info */}
                    <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderBottom: '1px solid #e5e7eb' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e' }}>
                            Raw Product
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ImageSeeker
                                src={raw?.image}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: 1,
                                    border: '1px solid #e5e7eb',
                                    objectFit: 'contain',
                                    backgroundColor: '#fff'
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {raw?.title || '-'}
                                </Typography>
                                {raw?.variant_raw && (
                                    <Chip
                                        label={raw.variant_raw}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            fontSize: '0.75rem',
                                            height: 22,
                                            fontWeight: 600,
                                            backgroundColor: '#e0e7ff',
                                            color: '#4338ca'
                                        }}
                                    />
                                )}
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        ID: {raw?.product_id}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Site: {raw?.site} | Brand: {raw?.brand}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Current Mapping */}
                    {currentSku && (
                        <Box sx={{ p: 2, backgroundColor: '#d1fae5', borderBottom: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: '#065f46' }}>
                                    Current Mapping
                                </Typography>
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<IconSeeker icon="link-slash" />}
                                    onClick={handleRemoveMapping}
                                    disabled={isSaving}
                                >
                                    Remove
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Avatar
                                    src={currentSku?.image}
                                    variant="rounded"
                                    sx={{ width: 50, height: 50, bgcolor: '#ecfdf5', cursor: 'zoom-in', '&:hover': { opacity: 0.8 } }}
                                    onClick={() => {
                                        imageComparisonModal.value = {
                                            title: raw?.title,
                                            variantRaw: raw?.variant_raw,
                                            rawImage: raw?.image,
                                            skuImage: currentSku?.image,
                                            skuName: currentSku?.name
                                        }
                                    }}
                                />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                        {currentSku?.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        SKU ID: {currentSku?.id_sku}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Search */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Filter SKUs... (use comma for AND: text1,text2)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSeeker icon="search" sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput('')}>
                                            <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            helperText={!isLoading && allSkuOptions.length > 0 ?
                                `Showing ${visibleSkuOptions.length} of ${filteredSkuOptions.length} SKUs${filteredSkuOptions.length !== allSkuOptions.length ? ` (${allSkuOptions.length} total)` : ''}` :
                                undefined
                            }
                        />
                    </Box>

                    {/* SKU List */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : !brandId ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                <IconSeeker icon="circle-exclamation" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography>
                                    No brand information available for this product
                                </Typography>
                            </Box>
                        ) : filteredSkuOptions.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                <IconSeeker icon="box-open" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography>
                                    {searchInput ? 'No SKUs match your filter' : 'No SKUs found for this brand'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2}>
                                    {visibleSkuOptions.map((sku) => (
                                        <Grid item xs={12} key={sku.skuID}>
                                            <SkuOption
                                                sku={sku}
                                                isSelected={selectedSkuId === sku.skuID}
                                                isSaving={isSaving}
                                                onSelect={() => setSelectedSkuId(sku.skuID)}
                                                onImageClick={() => handleImageClick(sku)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {hasMoreToShow && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setVisibleCount(prev => prev + 30)}
                                            startIcon={<IconSeeker icon="chevron-down" />}
                                        >
                                            Load more ({filteredSkuOptions.length - visibleCount} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid #e5e7eb',
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving || !selectedSkuId}
                            startIcon={isSaving ? <CircularProgress size={16} /> : <IconSeeker icon="check" />}
                        >
                            {isSaving ? 'Saving...' : 'Accept'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Drawer>
    )
}
